// apps/widget/src/screens/booking.screen.tsx
//
// Three modes, decided by the server from the tenant's onboarding choice:
//   redirect  deep link into their existing system, scan context prefilled
//   embed     Calendly/Acuity inside the report, so they never leave
//   callback  a form, for WhatsApp-only clinics
//
// The intent is recorded before any of them. The click is the thing we can
// actually observe.

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { useScan } from '../state';
import { PhoneField, defaultCountry, phoneE164, type PhoneValue } from '../components/phone-field';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type Mode = 'loading' | 'redirect' | 'embed' | 'callback' | 'done';

export function BookingScreen() {
  const { state, go } = useScan();
  const [mode, setMode] = useState<Mode>('loading');
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!state.scanId) return setMode('callback');

    void api
      .bookingCta(state.scanId)
      .then((result) => {
        setUrl(result.url ?? null);
        setMode(result.mode);

        // Opened in a new tab rather than navigating the iframe: the report
        // stays where it was, so they can come back to it.
        if (result.mode === 'redirect' && result.url) {
          window.open(result.url, '_blank', 'noopener');
        }
      })
      .catch(() => setMode('callback'));
  }, [state.scanId]);

  if (mode === 'loading') {
    return <div className="gs-skeleton" style={{ height: 280 }} aria-busy="true" />;
  }

  if (mode === 'embed' && url) {
    return (
      <div className="gs-stack">
        <iframe className="gs-booking-embed" src={url} title={t('booking.title')} />
      </div>
    );
  }

  if (mode === 'redirect' && url) {
    return (
      <div className="gs-stack gs-centred">
        <h1 className="gs-title">{t('booking.title')}</h1>
        {/* The popup may have been blocked, so the link is always visible too. */}
        <a className="gs-btn gs-btn--primary" href={url} target="_blank" rel="noreferrer noopener">
          {t('recommendations.book')}
        </a>
        <button className="gs-btn gs-btn--ghost" onClick={() => go('recommendations')}>
          {t('common.back')}
        </button>
      </div>
    );
  }

  return <CallbackForm onDone={() => setMode('done')} done={mode === 'done'} />;
}

function CallbackForm({ onDone, done }: { onDone: () => void; done: boolean }) {
  const { state } = useScan();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState<PhoneValue>(() => ({
    country: defaultCountry(state.config.tenant.country),
    number: '',
  }));
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [channel, setChannel] = useState<'phone' | 'whatsapp' | 'email'>('phone');
  const [dayPart, setDayPart] = useState<'morning' | 'afternoon' | 'evening' | 'any'>('any');
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return (
      <div className="gs-stack gs-centred">
        <div className="gs-tick" aria-hidden="true">✓</div>
        <h1 className="gs-title">{t('booking.requested', { clinic: state.config.tenant.name })}</h1>
      </div>
    );
  }

  const byEmail = channel === 'email';
  const contact = byEmail ? (EMAIL.test(email.trim()) ? email.trim() : null) : phoneE164(phone);
  const nameOk = name.trim().length >= 2;
  const emailProblem = byEmail && (emailTouched || attempted) && !contact ? t('booking.emailInvalid') : null;
  const nameProblem = attempted && !nameOk ? t('booking.nameRequired') : null;

  const submit = async (): Promise<void> => {
    setAttempted(true);
    // Every problem is shown at once, rather than a disabled button that
    // does not say what it is waiting for.
    if (!nameOk || !contact) return;
    setBusy(true);
    setError(null);
    try {
      await api.callbackRequest({
        ...(state.scanId ? { scanId: state.scanId } : {}),
        name: name.trim(),
        contact,
        channel,
        preferredDayPart: dayPart,
        // What they answered before the scan, so the clinic calls back knowing it.
        ...(Object.keys(state.intake).length ? { intake: state.intake } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.title'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="gs-stack"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h1 className="gs-title">{t('booking.callback')}</h1>

      <label className="gs-field">
        <span className="gs-field-label">{t('booking.name')}</span>
        <input
          className="gs-input"
          value={name}
          autoComplete="name"
          onChange={(e) => setName(e.target.value)}
          aria-invalid={nameProblem ? true : undefined}
        />
        {nameProblem && (
          <span className="gs-field-error" role="alert">
            {nameProblem}
          </span>
        )}
      </label>

      {/* How first, then where: the contact field follows the choice. */}
      <fieldset className="gs-field">
        <legend className="gs-field-label">{t('booking.channel')}</legend>
        <div className="gs-chips">
          {(['phone', 'whatsapp', 'email'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="gs-chip"
              aria-pressed={channel === option}
              data-selected={channel === option || undefined}
              onClick={() => setChannel(option)}
            >
              {t(`booking.via.${option}`)}
            </button>
          ))}
        </div>
      </fieldset>

      {byEmail ? (
        <label className="gs-field">
          <span className="gs-field-label">{t('booking.email')}</span>
          <input
            className="gs-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            aria-invalid={emailProblem ? true : undefined}
          />
          {emailProblem && (
            <span className="gs-field-error" role="alert">
              {emailProblem}
            </span>
          )}
        </label>
      ) : (
        <PhoneField
          label={channel === 'whatsapp' ? t('booking.whatsapp') : t('booking.phone')}
          value={phone}
          onChange={setPhone}
          showError={attempted}
        />
      )}

      <fieldset className="gs-field">
        <legend className="gs-field-label">{t('booking.when')}</legend>
        <div className="gs-chips">
          {(['morning', 'afternoon', 'evening', 'any'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="gs-chip"
              aria-pressed={dayPart === option}
              data-selected={dayPart === option || undefined}
              onClick={() => setDayPart(option)}
            >
              {t(`booking.${option}`)}
            </button>
          ))}
        </div>
      </fieldset>

      {error && (
        <p className="gs-error" role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="gs-btn gs-btn--primary" disabled={busy}>
        {t('booking.request')}
      </button>
    </form>
  );
}
